import type { ConsoleErrorReason, ConsoleErrorResponse } from "@wallyo/contracts/console";

/** The codes the server answers with (ConsoleErrorResponse). */
export type ServerErrorCode = ConsoleErrorResponse["error"]["code"];

/**
 * Codes the console produces itself, when there is no server answer to read:
 * - `network_error`: the request never got an answer (offline, timeout, DNS).
 * - `unexpected_response`: an answer arrived but is not the contract's shape.
 */
export type ClientErrorCode = "network_error" | "unexpected_response";

export type ConsoleErrorCode = ServerErrorCode | ClientErrorCode;

const SERVER_CODES: readonly ServerErrorCode[] = [
  "unauthenticated",
  "session_expired",
  "feature_not_enabled",
  "invalid_code",
  "too_many_attempts",
  "stale_version",
  "validation_error",
  "internal_error",
];

/**
 * Every failure the console shows carries what failed and the request ID that
 * links it to the server log and the audit entry (ADR-0016 §2.5).
 */
export class ConsoleApiError extends Error {
  readonly code: ConsoleErrorCode;
  /**
   * The server's specific reason (e.g. `effective_date_in_past`), when it
   * sent one. Screens switch on this rather than on the English message.
   */
  readonly reason: ConsoleErrorReason | null;
  readonly requestId: string | null;
  readonly status: number | null;
  /**
   * True when a write may or may not have reached the server. The screen says
   * so and offers to retry with the same idempotency key; it never resends alone.
   */
  readonly outcomeUnknown: boolean;

  constructor(options: {
    code: ConsoleErrorCode;
    message: string;
    reason?: ConsoleErrorReason | null;
    requestId?: string | null;
    status?: number | null;
    outcomeUnknown?: boolean;
  }) {
    super(options.message);
    this.name = "ConsoleApiError";
    this.code = options.code;
    this.reason = options.reason ?? null;
    this.requestId = options.requestId ?? null;
    this.status = options.status ?? null;
    this.outcomeUnknown = options.outcomeUnknown ?? false;
  }

  /** The session is gone: the console drops it and returns to sign-in. */
  get endsSession(): boolean {
    return this.code === "unauthenticated" || this.code === "session_expired";
  }
}

/** Reads a ConsoleErrorResponse body, or returns null if the body is something else. */
export function parseConsoleError(body: unknown): ConsoleErrorResponse["error"] | null {
  if (typeof body !== "object" || body === null || !("error" in body)) return null;
  const error = (body as { error: unknown }).error;
  if (typeof error !== "object" || error === null) return null;
  const { code, reason, message, requestId } = error as Record<string, unknown>;
  if (typeof code !== "string" || !SERVER_CODES.includes(code as ServerErrorCode)) return null;
  return {
    code: code as ServerErrorCode,
    ...(typeof reason === "string" && reason !== "" ? { reason: reason as ConsoleErrorReason } : {}),
    message: typeof message === "string" ? message : "",
    requestId: typeof requestId === "string" ? requestId : "",
  };
}
